// THE APPLY'S SPINNER, FED BY POLLING.
//
// The group apply moved to the worker (owner, 2026-10-02: "now move the
// apply to the worker with the spinners") so the request returns at once
// and the page has to follow the job instead of waiting for it.
//
// WHY POLLING AND NOT A SOCKET: there is no event channel to the browser
// in this app, and inventing one for a progress number would be a larger
// change than the feature. One small GET every few seconds while a job is
// live, and NOTHING when it is not - the interval is cleared, not left
// running at a slower rate, because a page left open overnight should cost
// zero requests.
//
// IT POLLS ONCE ON MOUNT whatever the state, because the common case is
// somebody reopening the page while an apply they started is still going.
// A spinner that only appears if you watched the button being pressed is
// not a spinner for a six-minute job.
import { useCallback, useEffect, useRef, useState } from "react";
import { getApplyStatus } from "api/tracking/fieldTrackingApi";

const BEAT_MS = 2500;
// How many dropped requests before the page stops asking and SAYS it has
// lost touch. A deploy drops one; a dead origin drops all of them, and a
// tab left open overnight should not poll for ever.
const MAX_MISSES = 20;

export default function useApplyRun(projectId) {
  const [run, setRun] = useState(null);
  // Lost touch with the server, as distinct from nothing running. The
  // spinner says so rather than spinning on a guess.
  const [unreachable, setUnreachable] = useState(false);
  const timer = useRef(null);
  const alive = useRef(true);

  // A READ HAS THREE OUTCOMES, NOT TWO, and collapsing two of them is the
  // bug the owner hit on 2026-10-02:
  //   { run }   the server answered, with a run or with null
  //   { failed: true }   the request did not land
  // The old version returned null for BOTH "no run" and "request failed",
  // and the caller read null as "nothing is running" and STOPPED POLLING
  // FOR GOOD. One dropped request - my own API restart, mid-poll - froze
  // his spinner on the word "queued" for six minutes while the job
  // finished 7 of 7 behind it.
  //
  // AND MY TEST FOR THIS PINNED THE WRONG HALF. It is called "A FAILED
  // READ IS NOT A FINISHED JOB" and it asserts the run STATE is kept -
  // which it was. It never asserted that polling CONTINUES, which is the
  // half that mattered.
  const read = useCallback(async () => {
    if (!projectId) return { run: null };
    try {
      const r = await getApplyStatus(projectId);
      if (!alive.current) return { run: null };
      setRun((r && r.run) || null);
      return { run: (r && r.run) || null };
    } catch (e) {
      // Keep what we had on screen - clearing it would make a dropped
      // request look like "it finished" - and tell the caller it was a
      // FAILURE so it keeps asking.
      return { failed: true };
    }
  }, [projectId]);

  useEffect(() => {
    alive.current = true;
    let stopped = false;
    let misses = 0;
    const tick = async () => {
      const r = await read();
      if (stopped || !alive.current) return;
      if (r.failed) {
        // KEEP ASKING. A dropped request says nothing about the job, and
        // the one thing we must not do is treat it as "finished" - which
        // is exactly what froze his spinner. Bounded, so a page left open
        // against a dead origin does not poll until the tab is closed;
        // and the gap widens so a restart is not hammered.
        misses += 1;
        if (misses <= MAX_MISSES) {
          timer.current = setTimeout(tick, BEAT_MS * Math.min(misses, 4));
          return;
        }
        setUnreachable(true);
        return;
      }
      misses = 0;
      setUnreachable(false);
      // Poll only while something is actually happening. `live` is false
      // for a finished run AND for one whose heartbeat went cold, so a
      // killed job stops the polling instead of spinning for ever.
      if (r.run && r.run.live) {
        timer.current = setTimeout(tick, BEAT_MS);
      }
    };
    tick();
    return () => {
      stopped = true;
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [read]);

  // Called when a job is STARTED, so the spinner appears on the click
  // rather than up to BEAT_MS later - the gap where a person presses the
  // button again is the gap that took the API down.
  const follow = useCallback(async () => {
    const r = await read();
    if (r.run && r.run.live && !timer.current) {
      timer.current = setTimeout(async function again() {
        const n = await read();
        // SAME THREE OUTCOMES HERE. The first version of this chain also
        // stopped on a failed read, so pressing Apply and then losing one
        // request left the spinner on screen for ever.
        if (n.failed || (n.run && n.run.live)) timer.current = setTimeout(again, BEAT_MS);
      }, BEAT_MS);
    }
    return r.run || null;
  }, [read]);

  return { run, refresh: read, follow, unreachable };
}
